// These functions run a callback if or when the user allows a category
// Example:
//
//  const termly = await import('../util/termly')
//  termly.allowPerformance(() => {
//    console.log('performance allowed')
//  })

import { setIfChanged } from "./cookies";

export const CATEGORIES = Object.freeze({
  ADVERTISING: "advertising",
  ANALYTICS: "analytics",
  ESSENTIAL: "essential",
  PERFORMANCE: "performance",
  SOCIAL_NETWORKING: "social_networking",
  UNCLASSIFIED: "unclassified",
});

export const ADVERTISING_COOKIES = Object.freeze([
  "_rc_uuid",
  "_obcid",
  "_tbcid",
  "_dicid",
  "_sacid",
  "_sncid",
  "_gclid",
  "_gbraid",
  "_wbraid",
  "_msclkid",
  "_fbclid",
  "_fbp",
  "_fbc",
]);

/**
 * Runs `callback` once the `termlyData` object is ready. A null value means
 * Termly hasn't reported yet, so it waits for the first `termlyDataUpdate`.
 *
 * @param {function} onNotification - Runs once `termlyData` is ready.
 */
export const ready = (onNotification) => {
  if (window.termlyData === null) {
    document.addEventListener("termlyDataUpdate", onNotification, {
      once: true,
    });
  } else {
    onNotification();
  }
};

// Run callback once `condition` holds. If it doesn't yet, wait for the next
// termlyDataUpdate and re-check. {once: true} keeps the listener set
// bounded — the recursive call re-registers a fresh listener only while the
// condition still fails.
const onCondition = (condition, onNotification) => {
  ready(() => {
    if (condition()) {
      onNotification();
    } else {
      document.addEventListener(
        "termlyDataUpdate",
        () => onCondition(condition, onNotification),
        { once: true }
      );
    }
  });
};

/**
 * Checks if a given category exists in the termlyData.categories array.
 *
 * @param {string} category - The category to check.
 * @returns {boolean} - True if the category exists, false otherwise.
 */
export const hasCategory = (category) => {
  // Array check fails closed on a malformed report: a string value would
  // otherwise substring-match category names via String.includes.
  const categories = window.termlyData?.categories;

  return Array.isArray(categories) && categories.includes(category);
};

const onAllowed = (category, onNotification) =>
  onCondition(() => hasCategory(category), onNotification);

export const allowAnalytics = (onNotification) =>
  onAllowed(CATEGORIES.ANALYTICS, onNotification);

export const allowAdvertising = (onNotification) =>
  onAllowed(CATEGORIES.ADVERTISING, onNotification);

export const isAllowedCookie = (cookie) => {
  // Check the cookie whitelist
  const cookies = window.termlyData?.cookies || [];

  if (cookies.some((e) => e.name === cookie)) {
    return true;
  }

  // Check window.termlyCookies, which holds every cookie Termly knows about,
  // and see whether the user allowed the given cookie's category
  if (window.termlyCookies) {
    const found = Object.values(window.termlyCookies).flatMap((array) =>
      array.filter((item) => item.name === cookie)
    );

    // The cookie counts as allowed only if the user consented to its category
    if (found.length > 0 && hasCategory(found[0].category)) {
      return true;
    }
  }

  return false;
};

/**
 * Run `callback` once consent covers the given cookie. A preset list maps some
 * cookies to categories in case Termly's own cookie list fails to load, so we
 * always handle them according to the user's preferences.
 *
 * @param {string} cookie - Name of the cookie to check.
 * @param {function} onNotification - Runs once consent covers the cookie.
 */
export const allowCookie = (cookie, onNotification) => {
  ready(() => {
    // Analytics cookies. (mtn-subscriber-status and the modal seen-flags
    // left this map when MTN-1444 reclassified them as functional/Essential
    // and routed them through the storage adapter.)
    if (
      [
        "mtn-ga4-client-id",
        "mtn-ga4-session-id",
        "mtn-user-id",
        "mtn-recommender-user-id",
      ].includes(cookie)
    ) {
      allowAnalytics(onNotification);
    } else if (ADVERTISING_COOKIES.includes(cookie)) {
      // Advertising Cookies
      allowAdvertising(onNotification);
    } else if (isAllowedCookie(cookie)) {
      // console.log(`${cookie} is allowed`)
      onNotification();
    } else {
      document.addEventListener(
        "termlyDataUpdate",
        () => allowCookie(cookie, onNotification),
        { once: true }
      );
    }
  });
};

/**
 * Deny-side view of Termly's sale/share opt-out flag. A truthy drifted value
 * remains restrictive, but only allowsDataSale() can authorize a sale gate
 * (MTN-1450, MTN-1439).
 *
 * @returns {boolean} - True when the visitor opted out of sale/share.
 */
export const doNotSell = () =>
  Boolean(window.termlyData?.consentState?.do_not_sell);

/**
 * True once Termly resolves without a sale/share opt-out. Termly applies GPC
 * itself, so an absent do_not_sell flag in a resolved category report means
 * no opt-out. Unresolved state still cannot satisfy a sale gate.
 *
 * @returns {boolean} - True when Termly resolved without an opt-out.
 */
export const allowsDataSale = () =>
  Array.isArray(window.termlyData?.categories) && !doNotSell();

/**
 * Run `callback` once Advertising is allowed AND do_not_sell is off. Vendors
 * that receive identifiers or that sell/share data (SmartAsset, Quantcast,
 * Meta advanced matching) require both signals, not just the Advertising
 * category (MTN-1442).
 *
 * @param {function} onNotification - Runs once both conditions hold.
 */
export const allowAdvertisingSale = (onNotification) =>
  onCondition(
    () => hasCategory(CATEGORIES.ADVERTISING) && allowsDataSale(),
    onNotification
  );

export const allowPerformance = (onNotification) =>
  onAllowed(CATEGORIES.PERFORMANCE, onNotification);

export const allowSocialNetworking = (onNotification) =>
  onAllowed(CATEGORIES.SOCIAL_NETWORKING, onNotification);

export const allowUnclassified = (onNotification) =>
  onAllowed(CATEGORIES.UNCLASSIFIED, onNotification);

/**
 * Sets the Termly UUID cookie from `window.termlyData.uuid`, waiting for Termly to supply a UUID if the termlyData object doesn't carry one yet.
 *
 * @function
 * @name setTermlyUUIDCookie
 *
 * @returns {undefined}
 *
 * @description
 * When `window.termlyData` has a uuid property, sets the Termly UUID cookie to that uuid.
 * Otherwise waits for the next 'termlyDataUpdate' event and calls setTermlyUUIDCookie again.
 * Cookie details:
 *   - Cookie name: `mtn-termly-uuid`
 *   - Domain: .moneytalksnews.com (consent-enforcement bridge covers every subdomain)
 *   - Max age: 90 days (MTN-1434 disposition), refreshed only when Termly
 *     issues a new UUID or on the first load after the cookie expires —
 *     never by a page load alone (MTN-321)
 *   - Path: /
 *   - Secure: true
 *   - SameSite: Lax
 */
export const setTermlyUUIDCookie = () => {
  if (window.termlyData?.uuid) {
    setIfChanged("mtn-termly-uuid", window.termlyData.uuid, {
      days: 90,
      parent: true,
    });
  } else {
    document.addEventListener("termlyDataUpdate", setTermlyUUIDCookie, {
      once: true,
    });
  }
};

/**
 * Report the current consent state for a category and every later change.
 * A null termlyData value means Termly hasn't resolved yet, so it waits for
 * Termly's first report.
 *
 * @param {string} category - The Termly category to observe.
 * @param {function} onNotification - Receives true when allowed and false when denied.
 * @returns {function} Stop observing the category.
 */
export const watchCategory = (category, onNotification) => {
  let hasReported = false;
  let lastState;

  const report = () => {
    if (window.termlyData === null) {
      return;
    }

    const state = hasCategory(category);

    if (hasReported && state === lastState) {
      return;
    }

    hasReported = true;
    lastState = state;
    onNotification(state);
  };

  document.addEventListener("termlyDataUpdate", report);
  report();

  return () => document.removeEventListener("termlyDataUpdate", report);
};

/**
 * Report when Termly fails to load or does not resolve before a deadline.
 * Resolving termlyData first cancels both unavailable paths.
 *
 * @param {function} onNotification - Receives the reason Termly is unavailable.
 * @param {{timeoutMs: number}} options - Deadline before reporting a timeout.
 * @returns {function} Stop waiting for an unavailable result.
 */
export const onUnavailable = (onNotification, { timeoutMs }) => {
  if (window.termlyData !== null) {
    return () => {
      // An already-resolved subscription has no listener to remove.
    };
  }

  let settled = false;

  const subscription = {
    cleanup: () => {
      clearTimeout(subscription.timeoutId);
      document.removeEventListener(
        "termlyDataUpdate",
        subscription.handleDataUpdate
      );
      document.removeEventListener(
        "termlyLoadFailed",
        subscription.handleLoadFailed
      );
    },
    handleDataUpdate: () => {
      if (window.termlyData !== null) {
        settled = true;
        subscription.cleanup();
      }
    },
    handleLoadFailed: () => subscription.settle("termly_load_failed"),
    settle: (reason) => {
      if (settled) {
        return;
      }

      settled = true;
      subscription.cleanup();
      onNotification(reason);
    },
    timeoutId: null,
  };

  document.addEventListener("termlyDataUpdate", subscription.handleDataUpdate);
  document.addEventListener("termlyLoadFailed", subscription.handleLoadFailed);
  subscription.timeoutId = setTimeout(
    () => subscription.settle("fallback_timer"),
    timeoutMs
  );

  return () => {
    settled = true;
    subscription.cleanup();
  };
};
